import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchCompetitionByMarket,
  fetchCompetitionByEvent,
  resolveCompetitionAlias,
  persistCompetitionAlias,
} from '../services/competition';
import {
  resetAliasPersistState,
  resetResolverState,
  clearLinkedCompetition,
} from '../store/competitionSlice';
import { fetchLinkedCompetition } from '../services/competition';
import { fetchTicketureAllEventIDs, fetchTicketureByOpenDate } from '../services/ticketure';
import { excludeCancelledEvents } from '../utils/excludeCancelledEvents';

const horizonDays = 30;
const marketRangeWeeks = 6;
const eventBackWindowDays = 30;
const eventForwardWindowDays = 30;
const eventIdRegex = /^(.*)_([A-Za-z]{2})_(\d{4}-\d{2}-\d{2})$/;

const buildMarketQueryFromInput = (rawValue) => {
  const trimmed = rawValue?.trim();
  if (!trimmed) return null;

  const now = dayjs();
  let alias = trimmed;
  let dateFrom = now.format('YYYY-MM-DD');
  let dateTo = now.add(marketRangeWeeks, 'week').format('YYYY-MM-DD');
  let derivedFromEvent = false;

  const match = trimmed.match(eventIdRegex);
  if (match) {
    derivedFromEvent = true;
    const citySegment = match[1];
    const stateSegment = match[2];
    const dateSegment = match[3];

    const city = citySegment
      .split('_')
      .filter(Boolean)
      .map((token) => (token.length > 0 ? token[0].toUpperCase() + token.slice(1) : token))
      .join(' ')
      .replace(/\s+/g, ' ');

    alias = `${city}, ${stateSegment.toUpperCase()}`;

    const anchor = dayjs(dateSegment);
    if (anchor.isValid()) {
      dateFrom = anchor.subtract(eventBackWindowDays, 'day').format('YYYY-MM-DD');
      dateTo = anchor.add(eventForwardWindowDays, 'day').format('YYYY-MM-DD');
    }
  }

  return {
    alias,
    dateFrom,
    dateTo,
    derivedFromEvent,
  };
};

const tryDate = (value) => {
  if (!value) return null;
  const date = dayjs(value);
  return date.isValid() ? date : null;
};

const extractEventStart = (event) =>
  tryDate(
    event?.competition_event?.start_date ||
      event?.competition_event?.start_at ||
      event?.start_date ||
      event?.start_at ||
      event?.event?.start_date ||
      event?.event?.start_at ||
      event?.dates?.start ||
      event?.schedule?.start_date,
  );

const extractEventEnd = (event) =>
  tryDate(
    event?.competition_event?.end_date ||
      event?.competition_event?.end_at ||
      event?.end_date ||
      event?.end_at ||
      event?.event?.end_date ||
      event?.event?.end_at ||
      event?.dates?.end ||
      event?.schedule?.end_date,
  );

const extractEventName = (event) =>
  event?.competition_event?.name ||
  event?.canonical_event?.name ||
  event?.competitor?.name ||
  event?.event?.name ||
  event?.name ||
  event?.title ||
  event?.show ||
  'Unnamed Event';

const extractVenue = (event) =>
  event?.competition_event?.venue ||
  event?.canonical_event?.venue ||
  event?.venue ||
  event?.location ||
  null;

const extractBadges = (entry) => {
  if (!entry) return [];
  if (Array.isArray(entry.badges)) return entry.badges;
  if (Array.isArray(entry?.competition_event?.badges)) return entry.competition_event.badges;
  return [];
};

const extractSources = (event) => {
  if (!event) return [];
  if (Array.isArray(event.sources)) return event.sources;
  if (Array.isArray(event?.competition_event?.sources)) return event.competition_event.sources;
  return [];
};

const canonicalIdFromCandidate = (candidate) =>
  candidate?.canonical_market_id ||
  candidate?.canonical_market?.id ||
  candidate?.market?.id ||
  candidate?.canonicalMarketId ||
  candidate?.id ||
  null;

const candidateDisplayName = (candidate) =>
  candidate?.canonical_market?.display_name ||
  candidate?.canonical_market?.market_name ||
  candidate?.market?.market_name ||
  candidate?.display_name ||
  candidate?.market_name ||
  candidate?.name ||
  candidate?.alias ||
  candidate?.city_state ||
  'Unnamed market';

const candidateScore = (candidate) => candidate?.score ?? candidate?.match_score ?? candidate?.similarity;

const candidateGeo = (candidate) => {
  const directGeo =
    candidate?.canonical_market?.geo ||
    candidate?.market?.geo ||
    candidate?.geo;

  if (directGeo) {
    return directGeo;
  }

  const lat =
    candidate?.market?.lat ??
    candidate?.market?.latitude ??
    candidate?.lat ??
    candidate?.latitude;
  const lng =
    candidate?.market?.lon ??
    candidate?.market?.lng ??
    candidate?.market?.longitude ??
    candidate?.lon ??
    candidate?.lng ??
    candidate?.longitude;

  if (lat !== undefined && lng !== undefined) {
    return { lat, lng };
  }

  return null;
};

const MarketSummary = ({ data }) => {
  if (!data) return null;

  const canonical = data.canonical_market || data.market || data.canonicalMarket;
  if (!canonical) return null;

  const name = canonical.display_name || canonical.market_name || canonical.name || canonical.alias || 'Canonical Market';
  const regionParts = [canonical.city, canonical.state, canonical.country_code].filter(Boolean);
  const timezone = canonical.timezone || canonical.tz || canonical.time_zone;
  const geo = canonical.geo || canonical.centroid;

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={1}>
          <Typography variant="h6">{name}</Typography>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            {regionParts.length > 0 && (
              <Chip label={regionParts.join(', ')} color="primary" size="small" />
            )}
            {timezone && <Chip label={timezone} size="small" />}
            {canonical.region && <Chip label={canonical.region} size="small" />}
            {canonical.calendar_position !== undefined && (
              <Chip label={`Calendar: ${canonical.calendar_position}`} size="small" />
            )}
          </Stack>
          {geo && (
            <Typography variant="body2" color="text.secondary">
              Geo: {geo.lat?.toFixed?.(3) ?? geo.latitude?.toFixed?.(3) ?? geo.lat ?? '—'},
              {' '}
              {geo.lng?.toFixed?.(3) ?? geo.longitude?.toFixed?.(3) ?? geo.lng ?? ''}
            </Typography>
          )}
          {data.alias && (
            <Typography variant="body2" color="text.secondary">
              Alias requested: {data.alias}
            </Typography>
          )}
          {Array.isArray(data.badges) && data.badges.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {data.badges.map((badge, idx) => (
                <Chip
                  key={`${badge?.label || badge}-${idx}`}
                  label={badge?.label ?? badge}
                  size="small"
                  color={(badge?.variant === 'warning' && 'warning') || (badge?.variant === 'success' && 'success') || 'default'}
                />
              ))}
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

const EventCard = ({ event }) => {
  const start = extractEventStart(event);
  const end = extractEventEnd(event);
  const name = extractEventName(event);
  const venue = extractVenue(event);
  const badges = extractBadges(event);
  const sources = extractSources(event);

  return (
    <Card variant="outlined" sx={{ minWidth: 240 }}>
      <CardContent>
        <Stack spacing={1}>
          <Typography variant="subtitle1" fontWeight={600}>
            {name}
          </Typography>
          {start && (
            <Typography variant="body2" color="text.secondary">
              {start.format('MMM D, YYYY')} {end && !end.isSame(start, 'day') ? `→ ${end.format('MMM D, YYYY')}` : ''}
            </Typography>
          )}
          {venue && (
            <Typography variant="body2" color="text.secondary">{venue}</Typography>
          )}
          {badges.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {badges.map((badge, idx) => (
                <Chip
                  key={`${badge?.label || badge}-${idx}`}
                  label={badge?.label ?? badge}
                  size="small"
                  color={(badge?.variant === 'warning' && 'warning') || (badge?.variant === 'success' && 'success') || 'default'}
                />
              ))}
            </Stack>
          )}
          {sources.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              Sources: {sources.map((source) => source?.name || source).join(', ')}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

const CandidateOption = ({ candidate, selected, onSelect }) => {
  const id = canonicalIdFromCandidate(candidate);
  const name = candidateDisplayName(candidate);
  const score = candidateScore(candidate);
  const geo = candidateGeo(candidate);
  const marketRef = candidate?.canonical_market || candidate?.market;
  const city = marketRef?.city;
  const state = marketRef?.state;
  const country = marketRef?.country_code;
  const method = candidate?.method;
  const distanceKm = candidate?.distance_km;
  const source = candidate?.source || candidate?.resolution_source || candidate?.link_source;

  return (
    <Card
      variant={selected ? 'elevation' : 'outlined'}
      elevation={selected ? 4 : 0}
      onClick={() => onSelect(candidate)}
      sx={{ cursor: 'pointer', p: 1 }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <Radio
          checked={selected}
          value={id ?? name}
          onChange={() => onSelect(candidate)}
        />
        <Stack spacing={0.5} flex={1}>
          <Typography variant="subtitle2" fontWeight={600}>
            {name}
          </Typography>
          {(city || state || country) && (
            <Typography variant="body2" color="text.secondary">
              {[city, state, country].filter(Boolean).join(', ')}
            </Typography>
          )}
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {typeof score === 'number' && (
              <Chip
                label={`Confidence ${(score * 100).toFixed(1)}%`}
                size="small"
                color={score >= 0.8 ? 'success' : score >= 0.6 ? 'warning' : 'default'}
              />
            )}
            {method && <Chip label={method} size="small" />}
            {Number.isFinite(distanceKm) && (
              <Chip label={`${distanceKm.toFixed(1)} km`} size="small" />
            )}
            {source && <Chip label={source} size="small" />}
          </Stack>
          {geo && (
            <Typography variant="caption" color="text.secondary">
              Geo {geo.lat}, {geo.lng}
            </Typography>
          )}
        </Stack>
      </Stack>
    </Card>
  );
};

const LinkedCompetitionPanel = ({
  dateFrom,
  dateTo,
  windowDays,
  onDateFromChange,
  onDateToChange,
  onWindowDaysChange,
  linkedState,
}) => {
  const dispatch = useDispatch();

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!dateFrom || !dateTo) return;
    dispatch(fetchLinkedCompetition({ dateFrom, dateTo, windowDays }));
  };

  const handleClear = () => {
    dispatch(clearLinkedCompetition());
  };

  const rows = linkedState?.rows || [];
  const summary = linkedState?.summary || {};

  return (
    <Stack spacing={2} component="section">
      <Typography variant="h6">Linked Competition Conflicts</Typography>

      <Stack
        component="form"
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        onSubmit={handleSubmit}
      >
        <DatePicker
          label="Date from"
          value={dateFrom ? dayjs(dateFrom) : null}
          onChange={(value) => onDateFromChange(value ? value.format('YYYY-MM-DD') : '')}
          slotProps={{ textField: { size: 'small' } }}
        />
        <DatePicker
          label="Date to"
          value={dateTo ? dayjs(dateTo) : null}
          onChange={(value) => onDateToChange(value ? value.format('YYYY-MM-DD') : '')}
          slotProps={{ textField: { size: 'small' } }}
        />
        <TextField
          label="Window days"
          type="number"
          size="small"
          value={windowDays}
          onChange={(event) => onWindowDaysChange(Number(event.target.value) || 0)}
          sx={{ width: 160 }}
          inputProps={{ min: 1, max: 90 }}
        />
        <Stack direction="row" spacing={1}>
          <Button type="submit" variant="contained" disabled={linkedState?.status === 'loading'}>
            {linkedState?.status === 'loading' ? 'Scanning…' : 'Run Scan'}
          </Button>
          {linkedState?.status === 'succeeded' && (
            <Button variant="text" onClick={handleClear}>
              Clear
            </Button>
          )}
        </Stack>
      </Stack>

      {linkedState?.status === 'failed' && (
        <Alert severity="error">{linkedState.error}</Alert>
      )}

      {linkedState?.status === 'succeeded' && (
        <Stack spacing={1}>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip label={`Our events: ${summary.totalEvents ?? rows.length}`} />
            <Chip label={`Conflicts: ${summary.totalCompetitors ?? rows.length}`} color="warning" />
            {summary.markets && Array.isArray(summary.markets) && summary.markets.length > 0 && (
              <Chip label={`Markets: ${summary.markets.length}`} />
            )}
          </Stack>

          {rows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No conflicts detected in the selected window.
            </Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
                    <th style={{ padding: '8px 12px' }}>Our Event</th>
                    <th style={{ padding: '8px 12px' }}>Competitor</th>
                    <th style={{ padding: '8px 12px' }}>Competitor Dates</th>
                    <th style={{ padding: '8px 12px' }}>Market</th>
                    <th style={{ padding: '8px 12px' }}>Window</th>
                    <th style={{ padding: '8px 12px' }}>Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr
                      key={`${row.our_event_id}-${row.competitor}-${index}`}
                      style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}
                    >
                      <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{row.our_event_id}</td>
                      <td style={{ padding: '8px 12px' }}>{row.competitor}</td>
                      <td style={{ padding: '8px 12px' }}>
                        {row.start_date}
                        {row.end_date && row.end_date !== row.start_date ? ` → ${row.end_date}` : ''}
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        {row.market?.market_name ||
                          [row.market?.city, row.market?.state].filter(Boolean).join(', ')}
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        {row.window_start} → {row.window_end}
                      </td>
                      <td style={{ padding: '8px 12px' }}>
                        {row.confidence != null ? `${(row.confidence * 100).toFixed(1)}%` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          )}
        </Stack>
      )}
    </Stack>
  );
};

const Competition = () => {
  const dispatch = useDispatch();
  const { byMarket, byEvent, resolver, aliasPersist } = useSelector((state) => state.competitions);
  const linkedState = useSelector((state) => state.competitions.linked);
  const ticketureDefaults = useSelector((state) => state.ticketure.defaultEventIDs);
  const allEventIds = useSelector((state) => state.ticketure.allEventIds);
  const companyName = useSelector((state) => state.company.company);
  const [aliasInput, setAliasInput] = useState('');
  const [aliasInputDirty, setAliasInputDirty] = useState(false);
  const [eventSearchInput, setEventSearchInput] = useState('');
  const [eventInputDirty, setEventInputDirty] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [linkedDateFrom, setLinkedDateFrom] = useState(dayjs().format('YYYY-MM-DD'));
  const [linkedDateTo, setLinkedDateTo] = useState(dayjs().add(90, 'day').format('YYYY-MM-DD'));
  const [linkedWindowDays, setLinkedWindowDays] = useState(21);
  const location = useLocation();
  const navigate = useNavigate();
  const initialisedFromQuery = useRef(false);
  const queryProvidedEventRef = useRef(false);
  const autoBootstrappedEventRef = useRef(false);

  const marketData = byMarket.data;
  const events = useMemo(
    () => (Array.isArray(marketData?.events) ? marketData.events : []),
    [marketData],
  );

  useEffect(() => {
    if (!companyName) return;
    const startDate = dayjs().format('YYYY-MM-DD');
    const endDate = dayjs().add(marketRangeWeeks, 'week').format('YYYY-MM-DD');
    dispatch(fetchTicketureByOpenDate({ startDate, endDate }));
  }, [dispatch, companyName]);

  useEffect(() => {
    if (!allEventIds || allEventIds.length === 0) {
      dispatch(fetchTicketureAllEventIDs());
    }
  }, [dispatch, allEventIds]);

  const upcomingEventOptions = useMemo(() => {
    if (!Array.isArray(ticketureDefaults)) return [];
    const now = dayjs();
    const horizon = now.add(marketRangeWeeks, 'week');
    const seen = new Set();
    const collected = [];

    ticketureDefaults.forEach((item) => {
      const eventId = item?.event_id;
      if (!eventId || seen.has(eventId)) return;

      const showDateRaw =
        item?.generalized_open_date ||
        item?.actual_start_date ||
        item?.event_date ||
        item?.start_date;
      const showDate = dayjs(showDateRaw);
      if (!showDate.isValid()) return;
      if (showDate.isBefore(now.subtract(1, 'day')) || showDate.isAfter(horizon)) return;

      seen.add(eventId);
      collected.push({
        eventId,
        showDate,
        marketName: item?.market_name || item?.market || item?.city || undefined,
      });
    });

    const allowedIds = new Set(excludeCancelledEvents(collected.map((entry) => entry.eventId)));

    return collected
      .filter((entry) => allowedIds.has(entry.eventId))
      .sort((a, b) => a.showDate.valueOf() - b.showDate.valueOf());
  }, [ticketureDefaults]);

  const upcomingEvents = useMemo(() => {
    const current = dayjs();
    const horizon = current.add(horizonDays, 'day');
    return events
      .map((event) => ({ event, start: extractEventStart(event) }))
      .filter(({ start }) => start && start.isAfter(current.subtract(1, 'day')) && start.isBefore(horizon))
      .sort((a, b) => a.start.valueOf() - b.start.valueOf())
      .map(({ event }) => event);
  }, [events]);

  const otherEvents = useMemo(() => {
    const horizon = dayjs().add(horizonDays, 'day');
    return events
      .map((event) => ({ event, start: extractEventStart(event) }))
      .filter(({ start }) => !start || start.isSame(horizon, 'day') || start.isAfter(horizon))
      .sort((a, b) => {
        if (!a.start && !b.start) return 0;
        if (!a.start) return 1;
        if (!b.start) return -1;
        return a.start.valueOf() - b.start.valueOf();
      })
      .map(({ event }) => event);
  }, [events]);

  const fallbackCandidates = useMemo(() => {
    if (!marketData?.candidates) return [];
    return Array.isArray(marketData.candidates) ? marketData.candidates : [];
  }, [marketData]);

  const resolverCandidates = useMemo(() => {
    if (!resolver?.data?.candidates) return [];
    return Array.isArray(resolver.data.candidates) ? resolver.data.candidates : [];
  }, [resolver]);

  const mergedCandidates = useMemo(() => {
    const merged = new Map();
    [...fallbackCandidates, ...resolverCandidates].forEach((candidate) => {
      const id = canonicalIdFromCandidate(candidate) || candidateDisplayName(candidate);
      if (!merged.has(id)) {
        merged.set(id, candidate);
      }
    });
    return Array.from(merged.values());
  }, [fallbackCandidates, resolverCandidates]);

  const triggerEventLookup = useCallback(
    (value, { replaceUrl = false } = {}) => {
      const trimmed = value?.trim();
      if (!trimmed) return;
      dispatch(fetchCompetitionByEvent({ eventId: trimmed }));

      setSelectedEventId(trimmed);
      setEventSearchInput(trimmed);
      setEventInputDirty(false);

      const derivedMarketQuery = buildMarketQueryFromInput(trimmed);
      if (derivedMarketQuery?.alias) {
        dispatch(
          fetchCompetitionByMarket({
            alias: derivedMarketQuery.alias,
            dateFrom: derivedMarketQuery.dateFrom,
            dateTo: derivedMarketQuery.dateTo,
          }),
        );
        setAliasInput(derivedMarketQuery.alias);
        setAliasInputDirty(false);
      }

      const params = new URLSearchParams();
      params.set('eventId', trimmed);
      const aliasForUrl = derivedMarketQuery?.alias ?? aliasInput.trim();
      if (aliasForUrl) {
        params.set('alias', aliasForUrl);
      }
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: replaceUrl });
    },
    [dispatch, aliasInput, navigate, location.pathname],
  );

  const triggerMarketFetch = useCallback(
    (rawAlias) => {
      const query = buildMarketQueryFromInput(rawAlias);
      if (!query?.alias) return null;
      dispatch(
        fetchCompetitionByMarket({
          alias: query.alias,
          dateFrom: query.dateFrom,
          dateTo: query.dateTo,
        }),
      );
      return query;
    },
    [dispatch],
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const aliasParam = params.get('alias');
    const eventParam = params.get('eventId');

    if (!initialisedFromQuery.current) {
      if (aliasParam) {
        const query = triggerMarketFetch(aliasParam);
        const nextAliasValue = query?.alias ?? aliasParam;
        setAliasInput(nextAliasValue);
        setAliasInputDirty(false);
      }

      if (eventParam) {
        queryProvidedEventRef.current = true;
        setEventSearchInput(eventParam);
        setSelectedEventId(eventParam);
        setEventInputDirty(false);
        dispatch(fetchCompetitionByEvent({ eventId: eventParam }));
      }

      initialisedFromQuery.current = true;
      return;
    }

    if (!aliasInputDirty && aliasParam) {
      const aliasQuery = buildMarketQueryFromInput(aliasParam);
      const aliasValue = aliasQuery?.alias ?? aliasParam;
      if (aliasValue && aliasValue !== aliasInput) {
        setAliasInput(aliasValue);
        setAliasInputDirty(false);
      }
    }

    if (eventParam) {
      queryProvidedEventRef.current = true;
      if (eventParam !== selectedEventId) {
        setSelectedEventId(eventParam);
        if (!eventInputDirty) {
          setEventSearchInput(eventParam);
        }
        dispatch(fetchCompetitionByEvent({ eventId: eventParam }));
      }
    }
  }, [location.search, aliasInput, aliasInputDirty, selectedEventId, eventInputDirty, triggerMarketFetch, dispatch]);

  useEffect(() => {
    if (autoBootstrappedEventRef.current) return;
    if (queryProvidedEventRef.current) return;
    if (selectedEventId) return;
    if (eventInputDirty || eventSearchInput) return;
    if (upcomingEventOptions.length === 0) return;

    const first = upcomingEventOptions[0];
    autoBootstrappedEventRef.current = true;
    setEventSearchInput(first.eventId);
    setSelectedEventId(first.eventId);
    setEventInputDirty(false);
    triggerEventLookup(first.eventId, { replaceUrl: true });
  }, [upcomingEventOptions, selectedEventId, eventInputDirty, eventSearchInput, triggerEventLookup]);

  useEffect(() => {
    if (aliasPersist.status === 'succeeded' && byMarket.alias) {
      const query = triggerMarketFetch(byMarket.alias);
      if (query?.alias && query.alias !== aliasInput) {
        setAliasInput(query.alias);
      }
      setAliasInputDirty(false);
      dispatch(resetResolverState());
    }
  }, [aliasPersist.status, byMarket.alias, triggerMarketFetch, dispatch, aliasInput]);

  const handleAliasSubmit = useCallback(
    (event) => {
      event.preventDefault();
      if (!aliasInput.trim()) return;
      setSelectedCandidateId(null);
      setSelectedCandidate(null);
      dispatch(resetResolverState());
      dispatch(resetAliasPersistState());
      const query = triggerMarketFetch(aliasInput);
      const nextAliasValue = query?.alias ?? aliasInput.trim();
      if (nextAliasValue !== aliasInput.trim()) {
        setAliasInput(nextAliasValue);
      }
      setAliasInputDirty(false);

      const params = new URLSearchParams(location.search);
      params.set('alias', nextAliasValue);
      if (params.get('eventId') && !selectedEventId.trim()) {
        params.delete('eventId');
      }
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: false });
    },
    [aliasInput, dispatch, location.pathname, location.search, navigate, selectedEventId, triggerMarketFetch],
  );

  const handleResolver = () => {
    if (!aliasInput.trim()) return;
    dispatch(resolveCompetitionAlias({ alias: aliasInput.trim() }));
  };

  const handlePersistAlias = () => {
    if (!selectedCandidate) return;
    const canonicalId = canonicalIdFromCandidate(selectedCandidate);
    if (!canonicalId) return;

    const metadata = {
      method: selectedCandidate.method || selectedCandidate.resolution_source,
      confidence: candidateScore(selectedCandidate),
      distance_km: selectedCandidate.distance_km,
      source: selectedCandidate.source,
    };

    dispatch(
      persistCompetitionAlias({
        alias: aliasInput.trim(),
        marketId: canonicalId,
        metadata,
      }),
    );
  };

  const handleCandidateSelect = (candidate) => {
    setSelectedCandidate(candidate);
    setSelectedCandidateId(canonicalIdFromCandidate(candidate) || candidateDisplayName(candidate));
  };

  const handleEventLookup = (event) => {
    event.preventDefault();
    triggerEventLookup(eventSearchInput);
  };

  const handleUpcomingEventClick = (option) => {
    triggerEventLookup(option.eventId);
  };

  const renderMarketContent = () => {
    if (byMarket.status === 'loading') {
      return (
        <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 160 }}>
          <CircularProgress />
        </Stack>
      );
    }

    if (byMarket.status === 'failed') {
      return (
        <Alert severity="error">
          {byMarket.error || 'Failed to load competition data.'}
        </Alert>
      );
    }

    if (!marketData) {
      return (
        <Alert severity="info">
          Enter a market alias to retrieve canonical competition coverage.
        </Alert>
      );
    }

    const adminRequired = Boolean(marketData.admin_required);

    return (
      <Stack spacing={3}>
        <MarketSummary data={marketData} />

        <Divider textAlign="left">Upcoming ({upcomingEvents.length})</Divider>
        {upcomingEvents.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No competitor events detected within the next {horizonDays} days.
          </Typography>
        ) : (
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
            {upcomingEvents.map((event, idx) => (
              <EventCard key={`upcoming-${idx}`} event={event} />
            ))}
          </Stack>
        )}

        <Divider textAlign="left">Future ({otherEvents.length})</Divider>
        {otherEvents.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No future competitor events listed beyond the {horizonDays}-day horizon.
          </Typography>
        ) : (
          <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap' }}>
            {otherEvents.map((event, idx) => (
              <EventCard key={`future-${idx}`} event={event} />
            ))}
          </Stack>
        )}

        {adminRequired && (
          <Alert severity="warning">
            This alias is not linked to a canonical market yet. Review the candidate suggestions below or run the resolver to find a match.
          </Alert>
        )}

        {(adminRequired || mergedCandidates.length > 0) && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Typography variant="h6">Candidate Markets</Typography>
              <Button
                variant="outlined"
                onClick={handleResolver}
                disabled={resolver.status === 'loading'}
              >
                {resolver.status === 'loading' ? 'Running resolver…' : 'Run Resolver'}
              </Button>
              {resolver.status === 'failed' && (
                <Typography variant="body2" color="error">
                  {resolver.error}
                </Typography>
              )}
            </Stack>

            {mergedCandidates.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No candidates returned yet. Try running the resolver.
              </Typography>
            )}

            {mergedCandidates.length > 0 && (
              <FormControl component="fieldset" sx={{ width: '100%' }}>
                <RadioGroup value={selectedCandidateId ?? ''}>
                  <Stack spacing={1.5}>
                    {mergedCandidates.map((candidate, idx) => (
                      <CandidateOption
                        key={canonicalIdFromCandidate(candidate) || `${candidateDisplayName(candidate)}-${idx}`}
                        candidate={candidate}
                        selected={
                          selectedCandidateId ===
                          (canonicalIdFromCandidate(candidate) || candidateDisplayName(candidate))
                        }
                        onSelect={handleCandidateSelect}
                      />
                    ))}
                  </Stack>
                </RadioGroup>
              </FormControl>
            )}

            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                onClick={handlePersistAlias}
                disabled={!selectedCandidate || aliasPersist.status === 'loading'}
              >
                {aliasPersist.status === 'loading' ? 'Saving…' : 'Persist Alias'}
              </Button>
              {aliasPersist.status === 'succeeded' && (
                <Alert
                  severity="success"
                  onClose={() => dispatch(resetAliasPersistState())}
                  sx={{ flex: 1 }}
                >
                  Alias saved successfully.
                </Alert>
              )}
              {aliasPersist.status === 'failed' && (
                <Alert
                  severity="error"
                  onClose={() => dispatch(resetAliasPersistState())}
                  sx={{ flex: 1 }}
                >
                  {aliasPersist.error || 'Unable to persist alias.'}
                </Alert>
              )}
            </Stack>
          </Stack>
        )}
      </Stack>
    );
  };

  const renderEventLookup = () => {
    return (
      <Stack spacing={2} component="form" onSubmit={handleEventLookup}>
        <Typography variant="h6">Lookup By Event ID</Typography>
        <Autocomplete
          freeSolo
          options={allEventIds || []}
          value={eventSearchInput}
          onInputChange={(event, newValue) => {
            setEventSearchInput(newValue || '');
            setEventInputDirty(true);
          }}
          onChange={(event, newValue) => {
            if (typeof newValue === 'string') {
              const trimmed = newValue.trim();
              triggerEventLookup(trimmed);
            } else if (!newValue) {
              setSelectedEventId('');
              setEventSearchInput('');
              setEventInputDirty(false);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Event ID"
              placeholder="Start typing to search"
              size="small"
            />
          )}
          sx={{ minWidth: 260 }}
        />
        {upcomingEventOptions.length > 0 && (
          <Stack spacing={1}>
            <Typography variant="subtitle2" color="text.secondary">
              Upcoming events (next 6 weeks)
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {upcomingEventOptions.map((option) => (
                <Chip
                  key={option.eventId}
                  label={`${option.eventId}${option.showDate ? ` • ${option.showDate.format('MMM D')}` : ''}`}
                  onClick={() => handleUpcomingEventClick(option)}
                  color={option.eventId === selectedEventId ? 'primary' : 'default'}
                  variant={option.eventId === selectedEventId ? 'filled' : 'outlined'}
                  size="small"
                />
              ))}
            </Stack>
          </Stack>
        )}
        <Stack direction="row" spacing={2} alignItems="center">
          <Button variant="outlined" type="submit" disabled={!eventSearchInput.trim() || byEvent.status === 'loading'}>
            {byEvent.status === 'loading' ? 'Searching…' : 'Search'}
          </Button>
          {byEvent.status === 'failed' && (
            <Typography variant="body2" color="error">
              {byEvent.error || 'Lookup failed.'}
            </Typography>
          )}
        </Stack>
        {byEvent.data && (
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={1}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {byEvent.data?.canonical_market?.display_name ||
                    byEvent.data?.canonical_market?.market_name ||
                    byEvent.data?.canonical_market?.name ||
                    'Canonical Market'}
                </Typography>
                {byEvent.data?.canonical_market?.city && (
                  <Typography variant="body2" color="text.secondary">
                    {byEvent.data.canonical_market.city}
                    {byEvent.data.canonical_market.state ? `, ${byEvent.data.canonical_market.state}` : ''}
                  </Typography>
                )}
                {Array.isArray(byEvent.data?.badges) && byEvent.data.badges.length > 0 && (
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    {byEvent.data.badges.map((badge, idx) => (
                      <Chip
                        key={`${badge?.label || badge}-${idx}`}
                        label={badge?.label ?? badge}
                        size="small"
                        color={(badge?.variant === 'warning' && 'warning') || (badge?.variant === 'success' && 'success') || 'default'}
                      />
                    ))}
                  </Stack>
                )}
                {byEvent.data?.competition_event && (
                  <Tooltip title="competition_event">
                    <Typography variant="caption" color="text.secondary">
                      Linked competition event ID: {byEvent.data.competition_event.id || byEvent.data.competition_event.uid || '—'}
                    </Typography>
                  </Tooltip>
                )}
              </Stack>
            </CardContent>
          </Card>
        )}
      </Stack>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={4}>
        <Box component="form" onSubmit={handleAliasSubmit}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <TextField
              label="Market alias"
              value={aliasInput}
              onChange={(event) => {
                setAliasInputDirty(true);
                setAliasInput(event.target.value);
              }}
              placeholder="e.g. Seattle, WA"
              required
              sx={{ minWidth: 260 }}
            />
            <Button type="submit" variant="contained" disabled={!aliasInput.trim() || byMarket.status === 'loading'}>
              {byMarket.status === 'loading' ? 'Searching…' : 'Search'}
            </Button>
            {byMarket.status === 'failed' && (
              <Typography variant="body2" color="error">
                {byMarket.error}
              </Typography>
            )}
          </Stack>
        </Box>

        <Box>{renderMarketContent()}</Box>

        <Divider />

        <Box>{renderEventLookup()}</Box>

        <Divider />

        <LinkedCompetitionPanel
          dateFrom={linkedDateFrom}
          dateTo={linkedDateTo}
          windowDays={linkedWindowDays}
          onDateFromChange={setLinkedDateFrom}
          onDateToChange={setLinkedDateTo}
          onWindowDaysChange={setLinkedWindowDays}
          linkedState={linkedState}
        />
      </Stack>
    </Box>
  );
};

export default Competition;
